import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import {
    ICallable,
    ICallableObject,
    IRequestCallback,
    IRequestOptions,
    ISubmitFormOptions,
    RequestService
} from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { Subscription } from 'rxjs';

/**
 * @deprecated Just do without it
 */
@Directive()
export abstract class AbstractFormComponent<T extends {}> implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    /**
     * Redirect location after successful submission (optional)
     */
    @Input()
    redirect?: string;

    /**
     * On success callback action (optional)
     */
    @Output()
    successCallback = new EventEmitter();

    /**
     * Form
     * important: you need to put #form="ngForm" on form element in template
     */
    @ViewChild('form', { static: false })
    form!: NgForm;

    @ViewChild('form', { read: ElementRef })
    formRef!: ElementRef;

    protected subscriptions = new Subscription();

    /**
     * Submission status object
     */
    status = new Status();

    /**
     * Form data to be passed to BE
     */
    data: Partial<T> = {};

    /**
     * Hook for request options
     * its optional, in most cases you don't need it
     */
    protected requestOptions?: IRequestOptions = {};

    /**
     * Submission path
     */
    protected abstract submitPath: string;

    /**
     * Submission success event
     * set in component if you want even to trigger
     */
    protected successEvent;

    initiated = false;

    /**
     * App entry
     */
    @NgInject(App)
    readonly app!: App;

    /**
     * Request service
     */
    @NgInject(RequestService)
    requestService!: RequestService;

    /**
     * Angular router
     */
    @NgInject(Router)
    router!: Router;

    arrowSubmit = (onSuccess: IRequestCallback = [], onError?: IRequestCallback) => {
        this.submit(onSuccess, onError);
    };

    constructor(protected ref: ChangeDetectorRef) {}

    detectChanges() {
        if (this.initiated) {
            this.ref.detectChanges();
        }
    }

    /**
     * On component init hook
     */
    ngOnInit() {
        this.reset();
        this.initiated = true;
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.initiated) {
            this.detectChanges();
        }
    }

    ngAfterViewInit() {
        if (this.form) {
            this.subscriptions.add(
                this.form.valueChanges!.subscribe(() => {
                    if (this.initiated) {
                        this.detectChanges();
                    }
                })
            );
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    protected getSubmitAction(): ICallable {
        return { path: this.submitPath, data: this.data };
    }

    /**
     * Resets form data
     */
    reset(): void {
        this.dataReset();
        this.detectChanges();
    }

    dataReset(): void {
        this.data = {};
    }

    /**
     * Submission action
     */
    submit(onSuccess: IRequestCallback = [], onError: IRequestCallback = []): void {
        const dataCopy = { ...this.data };

        if (!Array.isArray(onSuccess)) {
            onSuccess = [onSuccess];
        }

        onSuccess.unshift(response => this.onSubmitSuccess(response));

        if (!Array.isArray(onError)) {
            onError = [onError];
        }

        onError.unshift(response => this.onSubmitError(dataCopy as T, response));

        this.requestService.submitForm(
            this.form,
            this.status,
            this.getSubmitAction(),
            onSuccess,
            onError,
            this.requestOptions,
            this.submitFormOptions
        );
        this.detectChanges();
    }

    get submitFormOptions(): ISubmitFormOptions {
        return {};
    }

    /**
     * On succcess action
     */
    protected onSubmitError(data: T, response?: any): void {
        if (this.form) {
            this.form['submitAttempt'] = false;
        }
    }

    protected onSubmitSuccess(response?: any): void {
        this.successCallback.emit();

        this.reset();

        if (this.form) {
            this.form['submitAttempt'] = false;
        }

        if (this.redirect) {
            this.router.navigateByUrl(this.redirect);
        }

        if (this.successEvent) {
            this.app.events.broadcast(this.successEvent);
        }
    }
}
